import type { Foe } from '@/content/foes';
import { cx } from '@/lib/cx';
import { CodenameTiles } from './CodenameTiles';
import { Icon } from './Icon';
import { SignalMeter } from './SignalMeter';
import { Surface } from './Surface';
import styles from './FoeCodenameCard.module.css';

interface FoeCodenameCardProps {
  foe: Foe;
  revealedCount: number;
  /** Letter index to spotlight, usually the one just revealed. */
  highlight?: number | undefined;
  /** Shown in the readout slot beside or under the codename; the slot stays empty without it. */
  status?: string | undefined;
  /**
   * The floor's mastery estimate (0-1), shown as the "r signal" meter in the slot when there is no
   * status (an honest progress display: it rises with real progress and may dip).
   */
  signal?: number | undefined;
  /** `stacked`: slot under the codename (narrow columns); `inline`: side by side (wide rows). */
  layout?: 'stacked' | 'inline';
}

/**
 * The foe in disguise standing beside a riveted card with its partly uncovered codename. Every
 * element has a fixed position, so revealing letters or showing a status never moves anything.
 */
export function FoeCodenameCard({
  foe,
  revealedCount,
  highlight,
  status,
  signal,
  layout = 'stacked',
}: FoeCodenameCardProps) {
  const portrait = foe.portraits.disguised;
  return (
    <section className={cx(styles.root, styles[layout])} aria-live="polite">
      <Surface variant="metal" className={styles.card}>
        <CodenameTiles
          codename={foe.codename}
          revealedCount={revealedCount}
          highlight={highlight}
        />
        <Surface variant="well" className={styles.slot}>
          {status ? (
            <span className={styles.status}>{status}</span>
          ) : signal !== undefined ? (
            <SignalMeter value={signal} />
          ) : (
            <Icon name="globe" className={styles.watermark} />
          )}
        </Surface>
      </Surface>
      <img
        className={styles.portrait}
        src={portrait.url}
        width={portrait.width}
        height={portrait.height}
        alt=""
        draggable={false}
      />
    </section>
  );
}
